import { directionFromRelativePath } from './directionUtils'
import { extractAlxDataFromText } from './extractFromTxt'
import { applyCwCcwMode } from './mergeDirectional'
import { extractAlxPassName } from './passName'
import { sortAlxPassRows } from './sortPasses'
import type { AlxBatchResult, AlxPassRow, CwCcwMode } from './types'

export interface ProcessAlxFilesInput {
  files: File[]
  contents: Map<string, string>
  cwCcwMode: CwCcwMode
}

function relativePath(file: File): string {
  const webkit = (file as File & { webkitRelativePath?: string }).webkitRelativePath
  return webkit && webkit.length > 0 ? webkit : file.name
}

export function processAlxFiles(input: ProcessAlxFilesInput): AlxBatchResult {
  const { files, contents, cwCcwMode } = input
  const rows: AlxPassRow[] = []
  const errors: AlxBatchResult['errors'] = []
  const skippedFiles: string[] = []

  const txtFiles = files.filter((f) => f.name.toLowerCase().endsWith('.txt'))
  if (txtFiles.length === 0) {
    return {
      rows: [],
      errors: [{ fileName: '', message: 'No .txt files found in the selected folder.' }],
      skippedFiles: []
    }
  }

  for (const file of txtFiles) {
    const rel = relativePath(file)
    const text = contents.get(file.name) ?? contents.get(rel) ?? ''
    if (!text.trim()) {
      errors.push({ fileName: file.name, message: 'File is empty or could not be read.' })
      continue
    }

    const passName = extractAlxPassName(file.name)
    if (!passName) {
      skippedFiles.push(file.name)
      errors.push({
        fileName: file.name,
        message: `Could not extract pass name from filename.`
      })
      continue
    }

    const metrics = extractAlxDataFromText(text)
    if (!metrics) {
      errors.push({ fileName: file.name, message: 'Could not extract summary data from file.' })
      continue
    }

    const direction = directionFromRelativePath(rel)
    let passNameOut = passName
    if (cwCcwMode === 'separate' && direction) {
      passNameOut = `${passName} (${direction})`
    }

    rows.push({
      fileName: file.name,
      passName: passNameOut,
      direction: cwCcwMode === 'none' ? direction : null,
      metrics,
      parseWarnings: []
    })
  }

  const merged = applyCwCcwMode(rows, cwCcwMode)
  return {
    rows: sortAlxPassRows(merged),
    errors,
    skippedFiles
  }
}

export async function readAlxFileContents(files: File[]): Promise<Map<string, string>> {
  const map = new Map<string, string>()
  await Promise.all(
    files.map(
      (file) =>
        new Promise<void>((resolve) => {
          const reader = new FileReader()
          reader.onload = () => {
            map.set(file.name, String(reader.result ?? ''))
            const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath
            if (rel) map.set(rel, String(reader.result ?? ''))
            resolve()
          }
          reader.onerror = () => resolve()
          reader.readAsText(file)
        })
    )
  )
  return map
}
